/**
 * Загрузка документов в перечень приложений (решение от 25.09.2026).
 *
 * Любой распознанный документ показывает, какой пункт перечня он закрывает,
 * и по кнопке отмечает его собранным. Справки, которые полей дела
 * не заполняют, здесь же проверяются по содержанию:
 *
 *   • о несудимости — сведения об осуждении и об уголовном преследовании,
 *     а также та ли это справка: ФИО и дата рождения против должника;
 *   • Гостехнадзор и ГИМС — нет ли техники и судов, не было ли регистрационных
 *     действий за три года;
 *   • ФНС о сальдо ЕНС — отрицательное сальдо предлагается строкой
 *     «Обязательных платежей».
 *
 * Отметка «собран» ставится только по кнопке: документ с замечаниями
 * (судимость есть, справка на другого человека) собранным не считается,
 * пока юрист не решит.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { CREDIT_REPORT, newRowId, SECOND_CREDIT_REPORT, type ObligatoryPayment } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type AppendixImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';
import type { ImportHandle, Preloaded } from './import-handle.js';
import type { DocumentSubject } from './DebtorInnHint.js';

/*
  Сверка ФИО документа с делом. Часть в скобках — прежняя фамилия:
  МВД пишет «АЙНБИНДЕР (ЖУРАВЛЕВА) ЛИДИЯ АЛЕКСЕЕВНА», и без этого справка
  считалась выданной на другого человека, а пункт перечня не отмечался.
*/
/** Бюро по виду отчёта — те же записи справочника, что на сервере (documents.ts) */
const BUREAU_OF_REPORT: Record<string, string> = {
  'credit-report-scoring': 'bki-scoring',
  'credit-report-okb': 'bki-okb',
  'credit-report-nbki': 'bki-nbki',
};

const norm = (value: string) =>
  value.toUpperCase().replace(/Ё/g, 'Е').replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();

export const AppendixDocumentsImport = forwardRef<ImportHandle, {
  caseId: string;
  debtor: { fullName: string; birthDate: string | null; inn: string };
  /** Пункты текущего перечня: ключ, номер, текст */
  appendix: Array<{ key: string; num: number; text: string }>;
  checklist: Record<string, boolean>;
  readOnly: boolean;
  onMark: (keys: string[]) => void;
  onAddObligatory: (payment: ObligatoryPayment) => void;
  /** Встроенный режим (import-handle.ts): документы уже разобраны, кнопки — общие */
  preloaded?: Preloaded<AppendixImport>;
  /** ФИО должника в разобранных документах — блок ищет по ним прежнюю фамилию */
  onNames?: (names: string[]) => void;
  /** Второе бюро дела — его отчёт отмечает второй пункт «Кредитный отчёт» */
  secondBureauId?: string | null;
  /** Разобранные документы — блок ищет в них ИНН должника (29.10.2026) */
  onDocuments?: (documents: DocumentSubject[]) => void;
}>(function AppendixDocumentsImport({ caseId, debtor, appendix, checklist, readOnly, onMark, onAddObligatory, preloaded, onNames, onDocuments, secondBureauId }, ref) {
  const embedded = Boolean(preloaded);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<AppendixImport[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setMessage(null);
    const loaded: AppendixImport[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        loaded.push(await api.importAppendixDocument(caseId, file));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    setResults(loaded);
    setErrors(failed);
    setBusy(false);
    onNames?.(loaded.flatMap((result) => result.personNames ?? []));
    onDocuments?.(loaded);
  };

  /** Замечания по документу: проверки содержания и сверка с должником. */
  const problems = (r: AppendixImport): string[] => {
    const list = r.checks.filter((c) => !c.ok).map((c) => c.text);
    if (r.subject?.fullName && debtor.fullName && norm(r.subject.fullName) !== norm(debtor.fullName)) {
      list.push(`Документ выдан на «${r.subject.fullName}», а должник — «${debtor.fullName}»`);
    }
    if (r.subject?.birthDate && debtor.birthDate && r.subject.birthDate !== debtor.birthDate) list.push('Дата рождения в документе не совпадает с должником');
    if (r.subject?.inn && debtor.inn && r.subject.inn !== debtor.inn.replace(/\s/g, '')) list.push('ИНН в документе не совпадает с должником');
    return list;
  };

  const line = (key: string | null) => (key ? appendix.find((a) => a.key === key) : undefined);
  // Отчёт второго бюро закрывает свой пункт «Кредитный отчёт (бюро)» (01.11.2026)
  const keyOf = (r: AppendixImport) =>
    r.appendixKey === CREDIT_REPORT && secondBureauId && BUREAU_OF_REPORT[r.kind] === secondBureauId ? SECOND_CREDIT_REPORT : r.appendixKey;
  // Отмечаются только документы без замечаний и с пунктом в текущем перечне
  const markable = [...new Set(results.filter((r) => keyOf(r) && line(keyOf(r)) && problems(r).length === 0).map((r) => keyOf(r)!))].filter(
    (key) => !checklist[key],
  );

  const mark = () => {
    onMark(markable);
    const summary = `Отмечено собранными: ${markable.length}.`;
    setMessage(summary);
    return summary;
  };

  // Встроенный режим: документы приходят разобранными, отметка — общей кнопкой
  useEffect(() => {
    if (!preloaded) return;
    setResults(preloaded.results);
    setErrors(preloaded.errors);
    onNames?.(preloaded.results.flatMap((result) => result.personNames ?? []));
    onDocuments?.(preloaded.results);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onNames меняется на каждом рендере родителя
  }, [preloaded]);
  useImperativeHandle(ref, () => ({ accept: () => (markable.length ? mark() : null) }));

  return (
    <div className="report-import">
      {!embedded && (
        <DocumentUpload
          label="Загрузить документы клиента"
          busyLabel="Читаю документы…"
          hint="Отметит собранные приложения. Можно всю папку клиента сразу. Файлы нигде не сохраняются."
          busy={busy}
          disabled={readOnly}
          onFiles={(files) => void load(files)}
        />
      )}

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {message && <p className="hint">{message}</p>}

      {results.length > 0 && (
        <div className="draft">
          <table className="simple-table">
            <thead>
              <tr>
                <th>Документ</th>
                <th>Пункт перечня</th>
                <th>Проверка</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => {
                const item = line(keyOf(r));
                const issues = problems(r);
                return (
                  <tr key={i}>
                    <td>
                      {r.title}
                      {r.place && <div className="hint">Сведения из него загружаются в {r.place}</div>}
                    </td>
                    <td>
                      {!r.appendixKey ? (
                        <span className="hint">в перечне нет</span>
                      ) : item ? (
                        <>
                          {item.num}. {item.text}
                          {checklist[item.key] && <div className="saved">уже отмечен</div>}
                        </>
                      ) : (
                        <span className="hint">в перечне этого дела пункта нет</span>
                      )}
                    </td>
                    <td>
                      {r.checks
                        .filter((c) => c.ok)
                        .map((c) => (
                          <div key={c.text} className="saved">
                            {c.text}
                          </div>
                        ))}
                      {issues.map((text) => (
                        <div key={text} className="warning">
                          {text}
                        </div>
                      ))}
                      {r.taxDebt && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={readOnly}
                          onClick={() => {
                            // По налогам с недоимкой и пенями, если справка их детализирует (26.10.2026)
                            const { payments, amount, confirmingDocument } = r.taxDebt!;
                            const rows = payments ?? [{ name: 'Задолженность по единому налоговому счёту', arrears: amount, penalties: 0 }];
                            for (const row of rows) {
                              onAddObligatory({
                                id: newRowId(),
                                name: row.name,
                                arrears: row.arrears,
                                ...(row.penalties ? { penalties: row.penalties } : {}),
                                isBusiness: false,
                                confirmingDocument,
                              });
                            }
                            setMessage(
                              payments
                                ? `В «Обязательные платежи» добавлено строк: ${rows.length}, всего ${money(amount).digitsWithKopecks} ₽ — по налогам, недоимка и пени отдельно.`
                                : `В «Обязательные платежи» добавлено ${money(amount).digitsWithKopecks} ₽. Разбейте по налогам и пеням вручную: в справке нет детализации.`,
                            );
                          }}
                        >
                          Добавить в «Обязательные платежи»
                        </button>
                      )}
                      {r.checks.length === 0 && issues.length === 0 && <span className="hint">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {results.some((r) => problems(r).length > 0) && (
            <p className="warning">Документы с замечаниями собранными не отмечаются — решите по ним и отметьте вручную.</p>
          )}
          {!embedded && (
            <div className="toolbar">
              <button type="button" className="primary" disabled={readOnly || markable.length === 0} onClick={mark}>
                Отметить собранными ({markable.length})
              </button>
              <button type="button" className="link-button" onClick={() => setResults([])}>
                Закрыть
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
